import React from 'react'
import PropTypes from 'prop-types'
import Fuse from 'fuse.js'
import VibemapSearchContext from './context'
import * as apiRequests from "./api-requests"
import { formatEvents } from "./formatting"

import {
  dataStore,
  filterStore,
  locationStore,
  pageStore,
  setURLLocation,
} from "state/store-zustand";

import { geocodeAddress, getDistance, getPointFromLocation } from 'vibemap-constants/dist/map.js'
import { cities as allCities } from 'vibemap-constants/dist/helpers.js'
import { searchPlacesByName } from 'components/utils/testHelpers.js'

function VibemapSearchProvider({
  apiURL,
  children,
  doAutoSearch,
  doRedirect,
  doSearchGuides,
  doSearchEvents,
  errorCallback,
  userCoordinates,
  preferStoredCities,
  storedCities = allCities,
  preferStoredEvents,
  storedEvents,
  preferStoredGuides,
  storedGuides,
  preferStoredPlaces,
  shouldGeocode,
  storedPlaces,
  searchDebounceWait,
  shouldUpdateURL,
  wordpressURL,
}) {
  const [results, setResults] = React.useState({
    cities: [],
    events: [],
    guides: [],
    places: [],
  })

  const cityCurrent = dataStore((state) => state.cityCurrent)
  const locationCurrent = locationStore((state) => state.locationCurrent)

  const filters = filterStore(state => state.filters)
  const previousPage = pageStore(state => state.previousPage)


  const [cities, setCities] = React.useState(allCities)
  const [isLoadingCities, setIsLoadingCities] = React.useState(false)
  const [isLoadingEvents, setIsLoadingEvents] = React.useState(false)
  const [isLoadingGuides, setIsLoadingGuides] = React.useState(false)
  const [isLoadingPlaces, setIsLoadingPlaces] = React.useState(false)
  const [perPage, setPerPage] = React.useState(50)
  const [searchCities, setSearchCities] = React.useState([])
  const [searchTerm, setSearchTerm] = React.useState("")
  const [lastSearchTerm, setLastSearchTerm] = React.useState(null)
  const [secondarySearch, setSecondarySearch] = React.useState(null)
  const [searchBounds, setSearchBounds] = React.useState()
  const [searchDistance, setSearchDistance] = React.useState(20)
  const [searchCenter, setSearchCenter] = React.useState(userCoordinates)
  const [searchTime, setSearchTime] = React.useState(new Date().toISOString())
  const [searchDays, setSearchDays] = React.useState(14)
  const [searchCategory, setSearchCategory] = React.useState()
  const [searchVibes, setSearchVibes] = React.useState("")
  const [searchZoom, setSearchZoom] = React.useState(14)

  // workaround for debouncing async hooks
  const searchDebounceTimeout = React.useRef()

  React.useEffect(() => {
    const location = userCoordinates
      ? userCoordinates
      : `${locationCurrent?.longitude},${locationCurrent?.latitude}`

    if (location) setSearchCenter(location)
  }, [userCoordinates, locationCurrent])

  const geoPoint = searchCenter
    ? { point: searchCenter }
    : {}

  const searchOptions = React.useMemo(() => ({
    ...geoPoint,
    cities: searchCities.map(({ id }) => id).filter((id) => !!id).join(','),
    category: searchCategory,
    days: searchDays,
    bounds: searchBounds,
    distance: searchDistance,
    perPage,
    search: searchTerm,
    secondarySearch: secondarySearch,
    time: searchTime,
    vibes: searchVibes,
    zoom: searchZoom,
  }), [perPage, searchCategory, searchCities, searchBounds, searchDistance, searchCenter, searchTime, searchTerm, searchVibes, searchDays, searchZoom])

  const searchForCities = React.useCallback(
    async () => {
      const previousResults = results
      if (searchOptions.search === "") {
        setResults((previousResults) => ({
          ...previousResults,
          cities: storedCities || [],
        }))
        return
      }

      setIsLoadingCities(true)

      //console.log('DEBUG preferStoredCities && cities ', preferStoredCities,  cities);
      if (preferStoredCities && cities) {
        const fuzzySearchOptions = {
          threshold: 0.6,
          keys: ['title', 'name'],
        }

        let cities

        if (searchOptions.search) {
          const fuse = new Fuse(storedCities, fuzzySearchOptions)
          cities = fuse.search(searchOptions.search).map(({ item }) => item)
        } else {
          cities = storedCities
        }

        setResults((previousResults) => ({
          ...previousResults,
          cities,
        }))
      } else {
        const newCities = await apiRequests.getCities(searchOptions, apiURL)
        const citiesCombined = [...new Set([...allCities, ...newCities])]

        setCities(citiesCombined)

        setResults((previousResults) => ({
          ...previousResults,
          cities: citiesCombined,
        }))
      }

      setIsLoadingCities(false)
    },
    [
      apiURL,
      preferStoredCities,
      searchOptions,
      storedCities,
    ]
  )

  const searchEvents = React.useCallback(async () => {
    setIsLoadingEvents(true)
    const events = preferStoredEvents
      ? storedEvents
      : await apiRequests.getEvents(searchOptions, apiURL)

    const formattedEvents = formatEvents(events, searchOptions)

    setResults((previousResults) => ({
      ...previousResults,
      events: formattedEvents,
    }))
    setIsLoadingEvents(false)
  }, [apiURL, searchOptions, storedEvents, preferStoredEvents])

  const searchGuides = React.useCallback(
    async () => {
      setIsLoadingGuides(true)

      let guides

      if (preferStoredGuides && storedGuides && searchOptions.search) {
        const fuzzySearchOptions = {
          keys: ['title.rendered', 'content.rendered'],
        }

        const fuse = new Fuse(storedGuides, fuzzySearchOptions)
        guides = fuse.search(searchOptions.search)
      } else {
        guides = await apiRequests.getGuides(searchOptions, wordpressURL)
      }

      setResults((previousResults) => ({
        ...previousResults,
        guides,
      }))

      setIsLoadingGuides(false)
    },
    [
      searchOptions,
      preferStoredGuides,
      storedGuides,
      wordpressURL
    ]
  )

  const searchOptionDep = JSON.stringify(searchOptions)
  const storedPlacesDep = JSON.stringify(storedPlaces)
  const searchPlaces = React.useCallback(
    async () => {

      const shouldSearch = searchOptions.search != lastSearchTerm
      //console.log('DEBUG shouldSearch ', shouldSearch, lastSearchTerm, searchOptions);

      if (shouldSearch) {
        setIsLoadingPlaces(true)

        // Don't filter by location if we have a search term
        const options_general = {
          ...searchOptions,
          ordering: '-_score',
          //ordering: locationCurrent ? '-distance' : '-_score',
          location__geo_distance: cityCurrent ? `2000m__${cityCurrent.longitude}__${cityCurrent.latitude}` : undefined,
          dist: cityCurrent ? searchOptions?.distance : undefined,
          point: cityCurrent ? searchOptions?.point : undefined,
        }

        setLastSearchTerm(searchOptions.search)

        const places = preferStoredPlaces
          ? storedPlaces
          : await searchPlacesByName(options_general, apiURL)

        if (shouldGeocode) {
          const api_key = process.env.GATSBY_GOOGLE_PLACES_KEY
          let geocodeResults = searchOptions.search && searchOptions.search !== ""
            ? await geocodeAddress(
              api_key,
              searchOptions.search,
              //locationCurrent ? locationCurrent?.name : null
            )
            : null

          const num_results = geocodeResults?.data?.results?.results?.length ?? 0

          if (geocodeResults == null || num_results == 0) {
            handleError('No results from geocoding service.', geocodeResults)
            // Try again with the location name
            geocodeResults = await geocodeAddress(api_key, searchOptions.search + " " + locationCurrent?.name)
          }

          if (geocodeResults?.error) {
            handleError('There was a problem with the geocoding service.', geocodeResults)
          }

          if (geocodeResults && !geocodeResults.error) {

            const hasPlace = geocodeResults && geocodeResults.data && geocodeResults.data && geocodeResults.data.place !== null
            const place = hasPlace ? geocodeResults.data.place : null

            if (hasPlace) {
              // TODO: Make fuzzy + distance check global
              const fuse = new Fuse(places, {
                includeScore: true,
                keys: ['properties.name', 'properties.address'],
                threshold: 0.8
              })

              const fuzzyPlaceResults = fuse.search(place.name)

              /* FIXME: Sort by distance, but don't filter/hide */
              const nearbyPlaces = fuzzyPlaceResults.filter(fuzzyResult => {
                const vibemapPlace = fuzzyResult.item

                let distance = 1000
                try {
                  distance = getDistance(vibemapPlace.geometry.coordinates, place.geometry.coordinates)
                } catch (error) {
                  console.log(`error `, error)
                }
                return distance && distance < 0.5
              })

              const hasFuzzyPlace = fuzzyPlaceResults.length > 0
              const hasNeabyPlace = nearbyPlaces.length > 0
              //console.log(hasFuzzyPlace, hasNeabyPlace)

              // TODO: Do a fuzzy search on the places to merge
              if (hasFuzzyPlace && hasNeabyPlace) {
                // Merge place details with found record.
                const foundIndex = nearbyPlaces[0]?.refIndex ?? null;

                // Merge, favoring the geocoded result; cocat tips and vibes
                places[foundIndex].properties = {
                  ...places[foundIndex].properties,
                  ...place.properties,
                  // tips: places[foundIndex].properties.tips.concat(place.properties.tips)
                }

                //console.log('Google results match this ', foundIndex, place.properties, places[foundIndex].properties)

                //places.push(place)
              } else {
                // Put best result in the top of list
                places.unshift(place)
              }

            } else {
              console.log(`No place found `, geocodeResults);
            }

            const topResult = geocodeResults && geocodeResults.data.results && geocodeResults.data.results[0]
            //console.log(`TODO: combined places and geocodeResults `, geocodeResults, places);
          }
        }

        setResults((previousResults) => ({
          ...previousResults,
          places: places,
        }))

        setIsLoadingPlaces(false)
      } else {
        setIsLoadingPlaces(false)
      }
    },
    [
      apiURL,
      lastSearchTerm,
      locationCurrent,
      preferStoredPlaces,
      searchOptionDep,
      storedPlacesDep
    ]
  )

  const search = React.useCallback(
    () => {
      Promise.all([
        searchForCities(),
        searchPlaces(),
        doSearchEvents && searchEvents(),
        doSearchGuides && searchGuides()
      ])
    },
    [
      searchForCities,
      searchPlaces,
      searchEvents,
      searchGuides
    ]
  )

  const debouncedSearch = React.useCallback(
    () => {
      if (searchDebounceTimeout.current) {
        clearTimeout(searchDebounceTimeout.current)
      }

      searchDebounceTimeout.current = setTimeout(
        search,
        searchDebounceWait
      )
    },
    [search, searchDebounceWait]
  )

  const handleError = (message, error) => {
    if (errorCallback) {
      errorCallback(message)
    }
  }

  // clean debounce on unmount
  React.useEffect(() => {
    if (searchDebounceTimeout.current) {
      clearTimeout(searchDebounceTimeout.current)
    }
  }, [])

  // clean debounce on unmount
  React.useEffect(() => {
    if (doRedirect) {
      const hasSearch = (
        searchVibes && searchVibes != "" ||
        searchCategory && searchCategory != "" ||
        searchCities && searchCities.length > 0
      )

      if (hasSearch) {
        const newFilters = {
          ...filters,
          vibes: [searchVibes],
          activities: [searchCategory],
          cities: searchCities,
        }

        if (shouldUpdateURL) setURLLocation(newFilters, previousPage)
      }
    }
  }, [searchVibes, searchCategory, searchCities])

  React.useEffect(
    () => {
      if (!doAutoSearch || searchTerm?.length < 2) return;
      debouncedSearch();
    },
    [doAutoSearch, debouncedSearch]
  )

  const value = React.useMemo(() => ({
    actions: {
      setSearchBounds,
      setSearchCategory,
      setSearchCenter,
      setSearchCities,
      setSearchDistance,
      setPerPage,
      setSearchDays,
      setSearchTerm,
      setSecondarySearch,
      setSearchTime,
      setSearchVibes,
      setSearchZoom,
      search,
      debouncedSearch,
    },
    progress: {
      isLoadingCities,
      isLoadingEvents,
      isLoadingGuides,
      isLoadingPlaces,
    },
    filters: {
      vibes: searchVibes,
      category: searchCategory,
      cities: searchCities,
    },
    cities,
    searchTerm,
    secondarySearch,
    shouldUpdateURL,
    results,
  }), [
    // Memo dependencies
    setSearchBounds,
    setSearchCategory,
    setSearchCenter,
    setSearchCities,
    setSearchDistance,
    setPerPage,
    setSearchDays,
    setSearchTerm,
    setSearchTime,
    setSearchVibes,
    setSearchZoom,
    cities,
    search,
    searchTerm,
    secondarySearch,
    results,
    isLoadingCities,
    isLoadingEvents,
    isLoadingGuides,
    isLoadingPlaces,
    searchVibes,
    searchCategory,
    searchCities,
  ])

  return (
    <VibemapSearchContext.Provider value={value}>
      {children}
    </VibemapSearchContext.Provider>
  )
}

VibemapSearchProvider.propTypes = {
  apiURL: PropTypes.string,
  children: PropTypes.node.isRequired,
  doAutoSearch: PropTypes.bool,
  preferStoredCities: PropTypes.bool,
  storedCities: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  preferStoredPlaces: PropTypes.bool,
  storedPlaces: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  preferStoredEvents: PropTypes.bool,
  storedEvents: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  preferStoredGuides: PropTypes.bool,
  storedGuides: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
}

VibemapSearchProvider.defaultProps = {
  apiURL: 'https://api.vibemap.com/v0.3',
  doAutoSearch: false,
  doRedirect: true,
  shouldGeocode: true,
  preferStoredCities: false,
  storedCities: [],
  preferStoredPlaces: false,
  storedPlaces: [],
  preferStoredEvents: false,
  storedEvents: [],
  preferStoredGuides: false,
  searchDebounceWait: 1000,
  shouldUpdateURL: true,
  storedGuides: [],
  wordpressURL: 'https://cms.vibemap.com/wp-json/wp/v2',
}

export default VibemapSearchProvider